type CountryMetadata = {
  region: string;
  nationalLengths?: number[];
  formats?: { pattern: RegExp; groups: number[] }[];
};

const range = (from: number, to: number): number[] =>
  Array.from({ length: to - from + 1 }, (_, index) => from + index);

const COUNTRIES: Record<string, CountryMetadata> = {
  '1': { region: 'US', nationalLengths: [10], formats: [{ pattern: /^[2-9]\d{9}$/, groups: [3, 3, 4] }] },
  '7': { region: 'RU', nationalLengths: [10] },
  '20': { region: 'EG' },
  '27': { region: 'ZA', nationalLengths: [9] },
  '30': { region: 'GR', nationalLengths: [10] },
  '31': { region: 'NL', nationalLengths: [9] },
  '32': { region: 'BE', nationalLengths: [8, 9] },
  '33': { region: 'FR', nationalLengths: [9] },
  '34': { region: 'ES', nationalLengths: [9] },
  '36': { region: 'HU', nationalLengths: [8, 9] },
  '39': { region: 'IT', nationalLengths: range(6, 11) },
  '40': { region: 'RO', nationalLengths: [9] },
  '41': { region: 'CH', nationalLengths: [9], formats: [{ pattern: /^[2-9]\d{8}$/, groups: [2, 3, 2, 2] }] },
  '43': { region: 'AT', nationalLengths: range(4, 13) },
  '44': { region: 'GB', nationalLengths: [7, 9, 10] },
  '45': { region: 'DK', nationalLengths: [8] },
  '46': { region: 'SE', nationalLengths: range(6, 10) },
  '47': { region: 'NO', nationalLengths: [8] },
  '48': { region: 'PL', nationalLengths: [9] },
  '49': { region: 'DE', nationalLengths: range(5, 13) },
  '51': { region: 'PE' },
  '52': { region: 'MX' },
  '54': { region: 'AR' },
  '55': { region: 'BR' },
  '56': { region: 'CL' },
  '57': { region: 'CO' },
  '58': { region: 'VE' },
  '61': { region: 'AU', nationalLengths: range(5, 10) },
  '62': { region: 'ID' },
  '63': { region: 'PH' },
  '64': { region: 'NZ' },
  '65': { region: 'SG' },
  '66': { region: 'TH' },
  '81': { region: 'JP' },
  '82': { region: 'KR' },
  '84': { region: 'VN' },
  '86': { region: 'CN' },
  '90': { region: 'TR', nationalLengths: [10] },
  '91': { region: 'IN', nationalLengths: [10] },
  '92': { region: 'PK' },
  '98': { region: 'IR' },
  '212': { region: 'MA' },
  '213': { region: 'DZ' },
  '216': { region: 'TN' },
  '218': { region: 'LY' },
  '351': { region: 'PT', nationalLengths: [9] },
  '352': { region: 'LU' },
  '353': { region: 'IE', nationalLengths: range(7, 9) },
  '354': { region: 'IS' },
  '355': { region: 'AL' },
  '356': { region: 'MT' },
  '357': { region: 'CY' },
  '358': { region: 'FI' },
  '359': { region: 'BG' },
  '370': { region: 'LT', nationalLengths: [8] },
  '371': { region: 'LV', nationalLengths: [8] },
  '372': { region: 'EE' },
  '373': { region: 'MD', nationalLengths: [8] },
  '374': { region: 'AM' },
  '375': { region: 'BY' },
  '376': { region: 'AD' },
  '377': { region: 'MC' },
  '378': { region: 'SM' },
  '380': { region: 'UA', nationalLengths: [9] },
  '381': { region: 'RS' },
  '382': { region: 'ME' },
  '383': { region: 'XK' },
  '385': { region: 'HR' },
  '386': { region: 'SI', nationalLengths: [8] },
  '387': { region: 'BA' },
  '389': { region: 'MK' },
  '420': { region: 'CZ', nationalLengths: [9], formats: [{ pattern: /^[2-9]\d{8}$/, groups: [3, 3, 3] }] },
  '421': { region: 'SK', nationalLengths: [9], formats: [{ pattern: /^[2-9]\d{8}$/, groups: [3, 3, 3] }] },
  '423': { region: 'LI' },
  '852': { region: 'HK' },
  '886': { region: 'TW' },
  '961': { region: 'LB' },
  '962': { region: 'JO' },
  '963': { region: 'SY' },
  '964': { region: 'IQ' },
  '965': { region: 'KW' },
  '966': { region: 'SA' },
  '971': { region: 'AE' },
  '972': { region: 'IL', nationalLengths: [8, 9] },
  '974': { region: 'QA' },
  '994': { region: 'AZ' },
  '995': { region: 'GE' },
  '998': { region: 'UZ' },
};

const REGION_TO_CALLING_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(COUNTRIES).map(([callingCode, metadata]) => [metadata.region, callingCode]),
);

const E164_MAX_DIGITS = 15;
const MIN_NATIONAL_LENGTH = 4;

type ParsedNumber = {
  callingCode: string;
  nationalNumber: string;
};

function normalizeDigits(rawNumber: string): string | null {
  const stripped = rawNumber.replace(/[\s\-.()/~]/g, '');
  if (!/^\d+$/.test(stripped)) {
    return null;
  }
  return stripped;
}

function splitCallingCode(digits: string): ParsedNumber | null {
  if (digits.startsWith('0')) {
    return null;
  }

  for (let length = 1; length <= 3 && length < digits.length; length++) {
    const callingCode = digits.substring(0, length);
    if (COUNTRIES[callingCode]) {
      return { callingCode, nationalNumber: digits.substring(length) };
    }
  }

  return null;
}

function isPossibleNumber(parsed: ParsedNumber): boolean {
  const metadata = COUNTRIES[parsed.callingCode];
  if (!metadata) {
    return false;
  }

  const nationalLength = parsed.nationalNumber.length;
  if (parsed.callingCode.length + nationalLength > E164_MAX_DIGITS) {
    return false;
  }

  if (metadata.nationalLengths) {
    return metadata.nationalLengths.includes(nationalLength);
  }

  return nationalLength >= MIN_NATIONAL_LENGTH;
}

function tryParseE164(rawNumber: string): ParsedNumber | null {
  if (!rawNumber.startsWith('+')) {
    return null;
  }

  const digits = normalizeDigits(rawNumber.substring(1));
  if (!digits) {
    return null;
  }

  const parsed = splitCallingCode(digits);
  if (!parsed || !isPossibleNumber(parsed)) {
    return null;
  }

  return parsed;
}

function tryParseOldFormat(rawNumber: string): ParsedNumber | null {
  if (!rawNumber.startsWith('00')) {
    return null;
  }

  return tryParseE164(`+${rawNumber.substring(2)}`);
}

function tryParseWithoutPlusFormat(rawNumber: string): ParsedNumber | null {
  const withPlus = rawNumber.startsWith('+') ? rawNumber : `+${rawNumber}`;
  return tryParseE164(withPlus);
}

function tryParse(rawNumber: string): ParsedNumber | null {
  return tryParseE164(rawNumber) ?? tryParseOldFormat(rawNumber) ?? tryParseWithoutPlusFormat(rawNumber);
}

function formatNationalNumber(parsed: ParsedNumber, separator: string): string {
  const metadata = COUNTRIES[parsed.callingCode];
  const format = metadata?.formats?.find((f) => f.pattern.test(parsed.nationalNumber));
  if (!format) {
    return parsed.nationalNumber;
  }

  const parts: string[] = [];
  let offset = 0;
  for (const groupLength of format.groups) {
    parts.push(parsed.nationalNumber.substring(offset, offset + groupLength));
    offset += groupLength;
  }

  return parts.join(separator);
}

export class PhoneNumber {
  private readonly parsed: ParsedNumber;

  readonly value: string;

  constructor(value: string) {
    value = value.trim();
    if (!value) {
      throw new Error('Phone number must not be empty');
    }

    const parsed = tryParse(value);
    if (!parsed) {
      throw new Error(
        `Phone number "${value}" is invalid. Please provide your phone in the E.164 standard format. ` +
          "Phone number should start with a '+' sign, followed by a country code (1-3 digits), " +
          'followed by a subscriber number (max 12 digits). Example: +420601123456',
      );
    }

    this.parsed = parsed;
    this.value = `+${parsed.callingCode}${parsed.nationalNumber}`;
  }

  static tryInitialize(rawNumber: string | null | undefined, defaultRegion?: string | null): PhoneNumber | null {
    if (rawNumber == null) {
      return null;
    }

    try {
      let candidate = rawNumber.trim();

      if (defaultRegion != null && candidate && !candidate.startsWith('+') && !candidate.startsWith('00')) {
        const callingCode = REGION_TO_CALLING_CODE[defaultRegion.toUpperCase()];
        if (callingCode) {
          const digits = normalizeDigits(candidate);
          if (!digits) {
            return null;
          }
          const nationalNumber = digits.startsWith('0') ? digits.substring(1) : digits;
          candidate = `+${callingCode}${nationalNumber}`;
        }
      }

      return new PhoneNumber(candidate);
    } catch {
      return null;
    }
  }

  getRegionCode(): string {
    return COUNTRIES[this.parsed.callingCode]?.region ?? '';
  }

  toFormattedString(): string {
    return `+${this.parsed.callingCode} ${formatNationalNumber(this.parsed, ' ')}`;
  }

  getUrlForCall(): string {
    return `tel:+${this.parsed.callingCode}-${formatNationalNumber(this.parsed, '-')}`;
  }

  toString(): string {
    return this.value;
  }
}

export function canonicalPhoneNumber(rawNumber: string | null | undefined): PhoneNumber | null {
  const trimmed = rawNumber?.trim() ?? '';
  return trimmed.startsWith('+') ? PhoneNumber.tryInitialize(trimmed) : null;
}
